import { CarouselProps } from "@bookingcom/bui-react";
import { useFormatDateTime } from "@bookingcom/flights-core";
import { t, useI18n } from "@bookingcom/lingojs-react";
import useClientCachedURL from "../../../hooks/useClientCachedURL";
import useProfiler from "../../../hooks/useProfiler";
import { useCallback } from "react";

const useAlternativeFaresCarouselController = () => {
  const { isMobile } = useProfiler();
  const i18n = useI18n();
  const { formats } = useFormatDateTime(i18n);
  const { getLastSuccessfulSearchUrl } = useClientCachedURL();

  const getSubtitle = useCallback(() => {
    const lastSearchUrlPrams = new URLSearchParams(getLastSuccessfulSearchUrl());

    const fromLocation = lastSearchUrlPrams.get("fromLocationName");
    const toLocation = lastSearchUrlPrams.get("toLocationName");
    const numberOfTravellers = lastSearchUrlPrams.get("adults");
    const departDate = lastSearchUrlPrams.get("depart");
    const returnDate = lastSearchUrlPrams.get("return");

    if (!fromLocation || !toLocation || !departDate || !numberOfTravellers) {
      return undefined;
    }

    const route = i18n.trans(
      t("flights_bp_failed_no_payment_collected_route", {
        variables: {
          origin_city: fromLocation,
          destination_city: toLocation
        }
      })
    );

    const travellers = i18n.trans(
      t("flights_bp_failed_no_payment_collected_travellers", {
        variables: {
          num_travellers: numberOfTravellers
        },
        num_exception: 0
      })
    );

    let dates = "";

    if (returnDate) {
      dates = i18n.trans(
        t("flights_bp_failed_no_payment_collected_rt_multi", {
          variables: {
            departure_date: formats.flightDateWeekday(departDate),
            return_date: formats.flightDateWeekday(returnDate)
          }
        })
      );
    } else {
      dates = i18n.trans(
        t("flights_bp_failed_no_payment_collected_one_way", {
          variables: {
            flight_date: formats.flightDateWeekday(departDate)
          }
        })
      );
    }

    return [route, travellers, dates].join(" · ");
  }, [formats, getLastSuccessfulSearchUrl, i18n]);

  return {
    carouselSize: (isMobile() ? "largest" : "large") as CarouselProps["size"],
    carouselActionCta: i18n.trans(t("flights_bp_failed_view_all_cta")),
    nextButtonAriaLabel: i18n.trans(t("a11y_flights_failed_alternative_carousel_forward")),
    previousButtonAriaLabel: i18n.trans(t("a11y_flights_failed_alternative_carousel_back")),
    title: i18n.trans(t("flights_bp_failed_best_similar")),
    subtitle: isMobile() ? undefined : getSubtitle(),
    forceHideLastAvailableSeats: !isMobile()
  };
};

export default useAlternativeFaresCarouselController;
